import { cloneDeep, first, join, last, reverse } from 'lodash';
import { calculateTime, removePunctuation } from '../utils/functions';

// ***************************************************************************************
// onScriptChecker()
// permet de vérifier l'orthographe, la conjugaison, la grammaire sur tous les mots du texte
// ***************************************************************************************
export function onScriptChecker() {
  if (
    this.readonly ||
    !this.script ||
    !this.script.length ||
    this.correcting ||
    !this.grammalecte.lang
  ) {
    return;
  }

  this.correcting = true;

  if (this.grammalecte.lang === 'en') {
    this.onWordCheckerEnglish(this.script);
    return;
  }

  const linesToCheck = this.grammalecte.convertWordsToSentences(this.script);

  this.grammalecte
    .checkLines(linesToCheck)
    .then(() => {
      this.correcting = false;
      this.updateTotalWordsInError();
      this.onRefreshCanvas();
    })
    .catch((err) => {
      console.error('Erreur lors de la vérification du script :' + err);
      this.correcting = false;
    });
}

// ***************************************************************************************
// onWordChecker()
// permet de vérifier l'orthographe, la conjugaison, la grammaire sur les mots
// ***************************************************************************************
export function onWordChecker(linesToCheck) {
  if (this.readonly || this.correcting || !this.grammalecte.lang) {
    return;
  }

  this.correcting = true;

  this.grammalecte
    .checkLines(linesToCheck)
    .then(() => {
      this.correcting = false;
      this.updateTotalWordsInError();
      this.onRefreshCanvas();
    })
    .catch((err) => {
      console.error('Erreur lors de la vérification des mots :' + err);
      this.correcting = false;
    });
}

// ***************************************************************************************
// onWordCheckerEnglish()
// permet de vérifier l'orthographe sur les mots en anglais
// ***************************************************************************************
export function onWordCheckerEnglish(words) {
  this.correcting = true;

  this.grammalecte
    .checkWordsEnglish(words)
    .then(() => {
      this.correcting = false;
      this.updateTotalWordsInError();
      this.onRefreshCanvas();
    })
    .catch((err) => {
      console.error('Erreur lors de la vérification du script :' + err);
      this.correcting = false;
    });
}

// ***************************************************************************************
// onCloseContextMenu()
// permet de fermer le menu contextuel
// ***************************************************************************************
export function onCloseContextMenu() {
  if (this.state.contextMenu.display) {
    this.setState({
      contextMenu: {
        display: false,
        x: 0,
        y: 0,
        word: null,
      },
    });
  }
}

// ***************************************************************************************
// onShowContextMenu()
// permet d'ouvrir le menu contextuel
// ***************************************************************************************
export function onShowContextMenu(event) {
  if (
    this.readonly ||
    !this.script ||
    !this.script.length ||
    !this.currentWord
  ) {
    return;
  }
  
  event.preventDefault();

  if (!this.currentWord.suggests || !this.currentWord.suggests.length) {
    this.onCloseContextMenu();
    return;
  }

  let x = this.currentWord.endX;
  let y = this.currentWord.endY;

  const width = 200;

  let length = 3;
  if (this.currentWord.errorType === "spelling" || this.currentWord.errorType === "spelling-en") {
    length += this.currentWord.suggests.length;
  } else {
    length += this.currentWord.suggests[0].suggests.length;
  }

  // 3 options
  const height = length * 32;

  if (x + width > this.width) {
    x -= width;
  }

  if (y + height > this.height) {
    y -= height;
  }

  if (y < 0) {
    y = 0;
  }

  this.setState({
    contextMenu: {
      display: true,
      x,
      y,
      word: this.currentWord,
    },
  });
}

// ***************************************************************************************
// onApplyCorrection()
// permet d'appliquer la correction choisie du menu contextuel sur les mots
// ***************************************************************************************
export function onApplyCorrection(word, suggest, remplacement, checkSameWords = true) {
  if (word.errorType === 'spelling') {
    if (checkSameWords) {
      const sameWords = this.script.filter(
        (x) => removePunctuation(x.text) === removePunctuation(word.text)
      );
      if (sameWords.length > 1) {
        this.setState({
          popup: {
            ...this.state.popup,
            display: true,
            showIcon: true,
            text: {
              fr: `Vous avez ${sameWords.length
                } occurences contenant le mot ${removePunctuation(
                  word.text
                )}, voulez-vous appliquer cette correction à tous les mots ?`,
              en: `You have ${sameWords.length
                } occurrences containing the word ${removePunctuation(
                  word.text
                )}, do you want to apply this correction to all words?`,
            },

            buttons: [
              {
                text: {
                  fr: 'Oui',
                  en: 'Yes',
                },
                onclick: () => {
                  this.onClosePopup();
                  sameWords.forEach((w) => (w.suggests = [suggest]));
                  this.onApplyCorrectionAll(sameWords);
                },
              },
              {
                text: {
                  fr: 'Non',
                  en: 'No',
                },
                onclick: () => {
                  this.onClosePopup();
                  this.onApplyCorrection(word, suggest, remplacement, false);
                },
              },
            ],
          },
        });

        return;
      }
    }

    this.onApplyCorrectionForSpelling(word, suggest);
  }

  if (word.errorType === 'grammar') {
    this.onApplyCorrectionForGrammar(word, suggest, remplacement);
  }

  if (word.errorType === 'spelling-en') {
    this.onApplyCorrectionForSpellingEnglish(word, suggest, remplacement);
  }

  this.onCloseContextMenu();
  this.onChange(this.currentWord.startTime, this.currentWord.index);
}

export function onApplyCorrectionForSpelling(word, suggest, applyUndo = true) {
  if (applyUndo) {
    // UNDO
    this.addNewActionForUndo('onApplyCorrection', word.index);
  }

  const text = removePunctuation(word.text);
  word.text = word.text.replace(text, suggest);
}

export function onApplyCorrectionForGrammar(
  word,
  suggest,
  remplacement,
  applyUndo = true
) {
  const indexLine = word.parentIndexLine ?? word.indexLine;

  const wordsOfLine = this.getWordsByIndexLine(indexLine);

  const wordsToApply = wordsOfLine.filter((x) =>
    x.lettersToCheck.find(
      (x) =>
        x.indexLetterSentence >= suggest.start &&
        x.indexLetterSentence <= suggest.end
    )
  );

  const firstWord = first(wordsToApply);
  const lastWord = last(wordsToApply);

  // La phrase complète des mots sur lesquels on va appliquer la correction
  const sentenceToApply = join(
    wordsToApply.map((x) => x.text),
    ' '
  );

  const wordsSuggested = remplacement.split(' ');

  // Calculer la position de début de l'erreur par rapport au premier mot
  const offset = suggest.end - suggest.start;
  const startOffset =
    suggest.start - firstWord.lettersToCheck[0].indexLetterSentence;
  const endOffset = startOffset + offset;

  // La phrase complète de la correction suggérée
  const sentenceSuggested = join(wordsSuggested, ' ');

  const newSentence =
    sentenceToApply.substring(0, startOffset) +
    sentenceSuggested +
    sentenceToApply.substring(endOffset);

  const newWords = [];

  // on créé un nouveau tableau de mot et on recalcule le timecode
  newSentence.split(' ').forEach((text, index) => {
    if (index === 0) {
      const newWord = {
        ...firstWord,
        text: text,
        startTime: firstWord.startTime,
        endTime: lastWord.endTime,
        letters: [],
        suggests: [],
      };

      newWords.push(newWord);
    } else {
      const startTime = newWords[index - 1].startTime;

      let inOutTime = calculateTime(
        startTime,
        lastWord.endTime,
        sentenceToApply,
        text
      );

      const newWord = {
        ...firstWord,
        index: firstWord.index + index,
        newline: false,
        text: text,
        startTime: inOutTime,
        endTime: lastWord.endTime,
        letters: [],
        suggests: [],
      };

      newWords[index - 1].endTime = inOutTime;

      newWords.push(newWord);
    }
  });

  if (applyUndo) {
    // UNDO
    this.addNewActionForUndo(
      'onApplyCorrection',
      firstWord.index,
      wordsToApply.length,
      wordsSuggested.length
    );
  }

  // Supprimer de la liste et ajouter ces nouveaux mots dans la liste
  this.script.splice(firstWord.index, wordsToApply.length, ...newWords);

  // On lance le recalcul des index
  if (wordsToApply.length !== wordsSuggested.length) {
    this.recalculateIndexes();
  }
}

export function onApplyCorrectionForSpellingEnglish(
  word,
  suggest,
  remplacement,
  applyUndo = true
) {
  if (applyUndo) {
    // UNDO
    this.addNewActionForUndo('onApplyCorrection', word.index);
  }

  const textToReplace = word.text.substring(suggest.start, suggest.end);

  word.text = word.text.replace(textToReplace, remplacement);
}

export function onApplyCorrectionAll(words) {
  // UNDO
  this.addScriptToNewUndo('onApplyCorrectionAll');

  const wordsReversed = reverse(words);

  wordsReversed.forEach((word) => {
    const suggest = word.suggests[0];
    
    if (word.errorType === 'spelling') {
      this.onApplyCorrectionForSpelling(word, suggest, false);
    }

    if (word.errorType === 'grammar') {
      this.onApplyCorrectionForGrammar(
        word,
        suggest,
        suggest.suggests[0],
        false
      );
    }

    if (word.errorType === 'spelling-en') {
      this.onApplyCorrectionForSpellingEnglish(
        word,
        suggest,
        suggest.suggests[0],
        false
      );
    }

    this.onChange(word.startTime, word.index);
  });

  this.onCloseContextMenu();

  setTimeout(() => this.onScriptChecker(), 100);
}

// ***************************************************************************************
// onCheckLine()
// permet de vérifier les mots de la ligne entière (du début à la fin de la ligne sans saut de ligne)
// ***************************************************************************************
export function onCheckLine(indexLine) {
  if (this.readonly || !this.grammalecte.lang) {
    return;
  }

  const words = this.getWordsByIndexLine(indexLine);

  if (this.grammalecte.lang === 'en') {
    this.onWordCheckerEnglish(words);

    return;
  }

  const sentence = this.grammalecte.convertWordsToOneSentence(indexLine, words);

  this.onWordChecker([sentence]);
}

// ***************************************************************************************
// onCheckLineByWord()
// permet de vérifier les mots de la ligne entière (à partir de l'index ou de l'index parent de la ligne du mot)
// ***************************************************************************************
export function onCheckLineByWord(word) {
  if (this.readonly || !word || !this.grammalecte.lang) {
    return;
  }

  if (this.grammalecte.lang === 'en') {
    this.onWordCheckerEnglish([word]);
    return;
  }

  this.onCheckLine(word.parentIndexLine ?? word.indexLine);
}

// ***************************************************************************************
// onCheckOnLines()
// permet de vérifier les lignes à partir de l'index jusqu'à l'index
// ***************************************************************************************
export function onCheckOnLines(startIndexLine, endIndexLine) {
  if (this.readonly || !this.grammalecte.lang) {
    return;
  }

  const words = this.script.filter(
    (x) =>
      (x.indexLine >= startIndexLine || x.parentIndexLine >= startIndexLine) &&
      (x.indexLine <= endIndexLine || x.parentIndexLine <= endIndexLine)
  );

  if (this.grammalecte.lang === 'en') {
    this.onWordCheckerEnglish(words);
    return;
  }

  const linesToCheck = this.grammalecte.convertWordsToSentences(words);

  this.onWordChecker(linesToCheck);
}

// ***************************************************************************************
// onGoToWordInError()
// permet de naviguer d'erreur en erreur
// ***************************************************************************************
export function onGoToWordInError() {
  const wordsSuggested = this.script.filter(
    (x) => x.suggests && x.suggests.length
  );

  let currentIndex = wordsSuggested.findIndex(
    (x) => x.index === this.currentWord.index
  );
  if (currentIndex === -1 || currentIndex === wordsSuggested.length - 1) {
    currentIndex = 0;
  } else {
    currentIndex++;
  }

  const word = wordsSuggested[currentIndex];

  this.setPositionForCursor(word, true);
}

export function onAddWordToDictionary(suggests) {
  this.onCloseContextMenu();
  alert("Cette fonctionnalité n'a pas été encore implémentée.");
}

export function onIgnoreWordForCorrection(word) {
  const text = removePunctuation(word.text);

  this.grammalecte.ignoreWord(text);

  word.suggests = [];

  const words = this.script.filter(
    (x) => removePunctuation(x.text.toLowerCase()) === text.toLowerCase()
  );
  words.forEach((w) => (w.suggests = []));

  this.onCloseContextMenu();
  this.onScriptChecker();
}

export function onIgnoreSuggestForCorrection(word) {
  this.grammalecte.ignoreSuggestWord(word);
  word.suggests = [];

  this.updateTotalWordsInError();
  this.onCloseContextMenu();
}
